import { useEffect } from "react";

/**
 * SAFETY NET for a well-known Radix bug:
 *
 * Radix Dialog / AlertDialog / Popover sets `document.body.style.pointerEvents = "none"`
 * while a modal is open, and restores it on close. If a modal unmounts (route change,
 * re-render, rapid open/close) before Radix runs its cleanup, `pointer-events: none`
 * gets stuck on <body> and every downstream Popover / Select (including all zone
 * dropdowns) stops opening, clicks are silently swallowed by the body layer.
 *
 * This mounts once at the app root and watches body's inline style. Whenever it sees
 * `pointer-events: none` AND no Radix modal is currently open in the DOM, it clears
 * the style. Safe: while a real modal is open we leave the lock alone.
 *
 * DO NOT REMOVE without a full alternative: this is the guarantee that zone/select
 * dropdowns can never be permanently blocked by an orphaned overlay.
 */
export function BodyPointerEventsGuard() {
  useEffect(() => {
    const body = document.body;

    const anyRadixModalOpen = () =>
      document.querySelector(
        '[data-radix-focus-guard], [data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
      ) !== null;

    const clearIfStuck = () => {
      if (body.style.pointerEvents === "none" && !anyRadixModalOpen()) {
        body.style.pointerEvents = "";
      }
    };

    const observer = new MutationObserver(clearIfStuck);
    observer.observe(body, { attributes: true, attributeFilter: ["style"] });
    // Also observe DOM changes so we recheck after a modal unmounts.
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Initial sweep on mount, and on route transitions (best-effort via popstate).
    clearIfStuck();
    window.addEventListener("popstate", clearIfStuck);

    return () => {
      observer.disconnect();
      window.removeEventListener("popstate", clearIfStuck);
    };
  }, []);

  return null;
}
